import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, ArrowRight, ArrowDown } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { CTAButton } from "@/components/CTAButton";
import TrustBar from "@/components/TrustBar";
import GoogleReviews from "@/components/GoogleReviews";
import TestimonialsGrid from "@/components/TestimonialsGrid";
import EnquiryForm from "@/components/EnquiryForm";
import { CONTACT, PROJECTS, IMAGES, TESTIMONIALS, WIX, SPECIALISATIONS } from "@/data/content";

const HERO = IMAGES.heroPlaster;

const FEATURED_SERVICES = [
  { title: "Rendering", desc: "Precision acrylic and cement rendering that redefines the street presence of any home or facade.", image: "https://images.unsplash.com/photo-1523217582562-09d0def993a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", to: "/services/rendering", testid: "featured-rendering-cta" },
  { title: "Waterproofing", desc: "Certified membrane systems for wet areas, balconies and below-ground — the invisible layer that protects everything.", image: "https://images.unsplash.com/photo-1744217725753-3fe4a7801645?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", to: "/services/waterproofing", testid: "featured-waterproofing-cta" },
];

export default function Home() {
  const { scrollY } = useScroll();
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.12]);
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(true), []);

  const featured = PROJECTS.slice(0, 4);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden" data-testid="hero-section">
        <motion.div style={{ scale: heroScale, y: heroY }} className="absolute inset-0">
          <img src={HERO} alt="Microcement surface finish" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40" />
        </motion.div>

        <div className="relative h-full container-x flex flex-col justify-center">
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "110%" }} animate={{ y: loaded ? 0 : "110%" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-white font-extrabold uppercase leading-[0.85] tracking-tighter text-[18vw] md:text-[13vw] lg:text-[9.5rem]"
            >
              Surfaces
            </motion.h1>
          </div>
          <div className="overflow-hidden self-end lg:mr-8 -mt-2 lg:-mt-6">
            <motion.h1
              initial={{ y: "110%" }} animate={{ y: loaded ? 0 : "110%" }}
              transition={{ duration: 1, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-white font-extrabold uppercase leading-[0.85] tracking-tighter text-[18vw] md:text-[13vw] lg:text-[9.5rem]"
            >
              Precision
            </motion.h1>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 30 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-10 max-w-xl"
          >
            <p className="text-white/90 text-lg md:text-xl font-light leading-relaxed">
              Unique, hard-wearing surface finishes for modern spaces. Waterproofing, concrete repairs, rising damp treatment, crack injection, rendering, plastering and microcement.
            </p>
            <p className="text-white/60 mt-3 font-light">Specialist surface solutions and remediation across Perth & Regional WA.</p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <CTAButton to="/contact" testid="cta-button-hero-quote" variant="accent">Get a Free Quote</CTAButton>
              <a href={CONTACT.phoneHref} data-testid="cta-button-hero-phone" className="btn-sweep inline-flex items-center gap-3 border border-white/40 text-white hover:text-charcoal px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300">
                <Phone className="h-4 w-4" strokeWidth={1.5} /> {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60">
          <ArrowDown className="h-5 w-5 animate-bounce" strokeWidth={1.5} />
        </div>
      </section>

      <TrustBar dark />

      {/* SERVICES STRIP */}
      <section className="container-x py-24 md:py-32 lg:py-40" data-testid="services-strip">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-8">
            <Overline className="mb-6">Our Specialisations</Overline>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Specialist Disciplines.<br /><span className="text-sand">One Standard.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/services" data-testid="services-view-all" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-charcoal hover:text-sand transition-colors">
              All Services <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>

        {/* Rows 1 & 2 — six disciplines */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SPECIALISATIONS.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08}>
              <Link to={s.to} data-testid={s.testid} className="group block relative h-[420px] overflow-hidden">
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <span className="font-mono text-xs text-white/50 mb-2">{s.index}</span>
                  <h3 className="font-display text-2xl md:text-3xl font-bold mb-1">{s.title}</h3>
                  <p className="text-[0.7rem] uppercase tracking-[0.18em] text-white/60">{s.tagline}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {/* Row 3 — Microcement featured */}
        <Reveal delay={0.1}>
          <Link to="/services/microcement" data-testid="service-card-microcement" className="group mt-4 relative block h-[320px] overflow-hidden">
            <img src={WIX.p1} alt="Microcement" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[800ms] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 to-black/30" />
            <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-center text-white max-w-2xl">
              <span className="font-mono text-xs text-white/50 mb-2">07</span>
              <h3 className="font-display text-4xl md:text-5xl font-bold mb-2">Microcement</h3>
              <p className="text-white/70 font-light">A continuous, jointless surface system applied at 2–3mm. A refined finish for floors, walls, and wet areas.</p>
              <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-sand">Explore Microcement <ArrowRight className="h-4 w-4" /></span>
            </div>
          </Link>
        </Reveal>
      </section>

      {/* FEATURED SERVICES — Rendering & Waterproofing */}
      <section className="grid md:grid-cols-2" data-testid="featured-service">
        {FEATURED_SERVICES.map((f) => (
          <div key={f.title} className="group relative h-[60vh] min-h-[420px] overflow-hidden">
            <img src={f.image} alt={f.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
            <div className="relative h-full container-x flex flex-col justify-center text-white">
              <Overline light className="mb-4">Featured Service</Overline>
              <h2 className="font-display text-5xl md:text-6xl font-extrabold mb-6">{f.title}</h2>
              <p className="text-white/80 text-lg font-light leading-relaxed mb-8 max-w-md">{f.desc}</p>
              <CTAButton to={f.to} testid={f.testid} variant="accent" className="self-start">Explore {f.title}</CTAButton>
            </div>
          </div>
        ))}
      </section>

      {/* FEATURED PROJECTS */}
      <section className="container-x py-24 md:py-32" data-testid="featured-projects">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <Overline className="mb-6">Featured Work</Overline>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">A Gallery of<br />Precision</h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/portfolio" data-testid="projects-view-all" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-charcoal hover:text-sand transition-colors">
              View All Projects <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <Link to={`/portfolio/${p.id}`} data-testid={`featured-project-${p.id}`} className="group block relative aspect-[3/4] overflow-hidden">
                <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[800ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 p-5 text-white translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <div className="text-[0.65rem] uppercase tracking-[0.18em] text-sand">{p.finish}</div>
                  <div className="font-display text-lg font-bold">{p.title}</div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS + GOOGLE REVIEWS */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="home-testimonials">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-8">
              <Overline className="mb-6">Client Testimonials</Overline>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">The Work Speaks.</h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/testimonials" data-testid="testimonials-view-all" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-charcoal hover:text-sand transition-colors">
                View All Testimonials <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-2">
              <TestimonialsGrid items={TESTIMONIALS.slice(0, 3)} />
            </div>
            <div className="lg:col-span-1">
              <GoogleReviews />
            </div>
          </div>
        </div>
      </section>

      {/* CTA + FORM */}
      <section className="bg-white py-24 md:py-32" data-testid="home-enquiry">
        <div className="container-x grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Overline className="mb-6">Start Your Project</Overline>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-8">Let's Build<br />Something<br /><span className="text-sand">Remarkable.</span></h2>
            <p className="text-taupe font-light leading-relaxed max-w-md mb-10">Share your project details and we'll provide a comprehensive, obligation-free quote within one business day.</p>
            <a href={CONTACT.phoneHref} className="inline-flex items-center gap-3 font-display text-3xl font-bold hover:text-sand transition-colors">
              <Phone className="h-6 w-6 text-sand" strokeWidth={1.5} /> {CONTACT.phone}
            </a>
            <p className="text-xs uppercase tracking-[0.18em] text-taupe mt-3">{CONTACT.hours}</p>
            <a href="mailto:admin@cemtex.com.au" data-testid="home-enquiry-email" className="inline-flex items-center gap-3 text-lg font-light text-taupe hover:text-sand transition-colors mt-4">
              <Mail className="h-5 w-5 text-sand" strokeWidth={1.5} /> admin@cemtex.com.au
            </a>
          </div>
          <div>
            <EnquiryForm />
          </div>
        </div>
      </section>
    </div>
  );
}
